import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';

// Pages
import Agents from './pages/Agents';
import Calls from './pages/Calls';
import Leads from './pages/Leads';
import QADashboard from './pages/QADashboard';
import QAMetrics from './pages/QAMetrics';
import CallAnalysis from './pages/CallAnalysis';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import QAReview from './pages/QAReview';
import CallReview from './pages/CallReview';
import ComparativeAnalysis from './pages/ComparativeAnalysis';
import AIModels from './pages/AIModels';
import AIPrompts from './pages/AIPrompts';
import Users from './pages/Users';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Tenants from './pages/Tenants';
import ServiceProviders from './pages/ServiceProviders';
import LeadSubmissions from './pages/LeadSubmissions';
import ScheduledSubmissions from './pages/ScheduledSubmissions';
import Billing from './pages/Billing';
import PredictionsDashboard from './pages/PredictionsDashboard';
import AgentDashboard from './pages/AgentDashboard';

// Components
import RequireAuth from './components/RequireAuth';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
            <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/agents" element={<ProtectedRoute><Agents /></ProtectedRoute>} />
            <Route path="/calls" element={<ProtectedRoute><Calls /></ProtectedRoute>} />
            <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
            <Route path="/qa-dashboard" element={<ProtectedRoute><QADashboard /></ProtectedRoute>} />
            <Route path="/qa-metrics" element={<ProtectedRoute><QAMetrics /></ProtectedRoute>} />
            <Route path="/call-analysis" element={<ProtectedRoute><CallAnalysis /></ProtectedRoute>} />
            <Route path="/qa-review" element={<ProtectedRoute><QAReview /></ProtectedRoute>} />
            <Route path="/qa-review/:callId" element={<ProtectedRoute><CallReview /></ProtectedRoute>} />
            <Route path="/qa-comparative/:callIds" element={<ProtectedRoute><ComparativeAnalysis /></ProtectedRoute>} />
            <Route path="/ai-models" element={<ProtectedRoute><AIModels /></ProtectedRoute>} />
            <Route path="/ai-prompts" element={<ProtectedRoute><AIPrompts /></ProtectedRoute>} />
            <Route path="/tenants" element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/service-providers" element={<ProtectedRoute><ServiceProviders /></ProtectedRoute>} />
            <Route path="/lead-submissions" element={<ProtectedRoute><LeadSubmissions /></ProtectedRoute>} />
            <Route path="/scheduled-submissions" element={<ProtectedRoute><ScheduledSubmissions /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/predictions" element={<ProtectedRoute><PredictionsDashboard /></ProtectedRoute>} />
            <Route path="/agent-dashboard" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;